import { z } from "zod";
import { useForm } from "react-hook-form";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAtom } from "jotai";
import { toast } from "sonner";
import { appearanceAtom } from "@/stores/appearance-atom";
import { cn } from "@/lib/utils";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

const appearanceFormSchema = z.object({
  theme: z.enum(["light", "dark"], {
    required_error: "Please select a theme.",
  }),
  font: z.enum(["inter", "poppins", "geist-mono"], {
    invalid_type_error: "Select a font",
    required_error: "Please select a font.",
  }),
  color: z.enum(["default", "nature", "retro"], {
    invalid_type_error: "Select a color palette",
    required_error: "Please select a color palette.",
  }),
});

type AppearanceFormValues = z.infer<typeof appearanceFormSchema>;

export function AppearanceForm() {
  const [appearance, setAppearance] = useAtom(appearanceAtom);

  const form = useForm<AppearanceFormValues>({
    resolver: zodResolver(appearanceFormSchema),
    defaultValues: {
      theme: appearance.theme,
      font: appearance.font,
      color: appearance.color,
    },
  });

  function onSubmit(data: Readonly<AppearanceFormValues>) {
    setAppearance((prev) => ({
      ...prev,
      theme: data.theme,
      font: data.font,
      color: data.color,
    }));

    toast.success("Preferences updated", {
      description: "Your appearance settings have been saved successfully.",
    });
  }

  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          void form.handleSubmit(onSubmit)(e);
        }}
        className="space-y-8"
      >
        <FormField
          control={form.control}
          name="font"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Font</FormLabel>
              <div className="relative w-max">
                <FormControl>
                  <select
                    className={cn(
                      buttonVariants({ variant: "outline" }),
                      "w-[200px] appearance-none font-normal",
                    )}
                    {...field}
                  >
                    <option value="inter">Inter</option>
                    <option value="poppins">Poppins</option>
                    <option value="geist-mono">Geist Mono</option>
                  </select>
                </FormControl>
                <ChevronDownIcon className="absolute right-3 top-2.5 h-4 w-4 opacity-50" />
              </div>
              <FormDescription>
                Choose a font for the dashboard.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="theme"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">
                  {field.value === "dark" ? "Dark" : "Light"} Theme
                </FormLabel>
                <FormDescription>
                  Switch between dark and light mode.
                </FormDescription>
              </div>
              <FormControl>
                <div className="relative">
                  <button
                    type="button"
                    role="switch"
                    aria-checked={field.value === "dark"}
                    onClick={() =>
                      field.onChange(field.value === "dark" ? "light" : "dark")
                    }
                    className={cn(
                      "focus-visible:ring-ring focus-visible:ring-offset-background group peer inline-flex h-[40px] w-[80px] shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
                      field.value === "dark" ? "bg-primary" : "bg-input",
                    )}
                  >
                    <span
                      className={cn(
                        "bg-background pointer-events-none flex aspect-square h-full w-[36px] items-center justify-center rounded-full shadow-sm ring-0 transition-transform duration-300",
                        field.value === "dark"
                          ? "translate-x-[40px]"
                          : "translate-x-0",
                      )}
                    >
                      {field.value === "dark" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="text-primary h-4 w-4"
                        >
                          <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="h-4 w-4 text-amber-500"
                        >
                          <circle cx="12" cy="12" r="4" />
                          <path d="M12 2v2" />
                          <path d="M12 20v2" />
                          <path d="m4.93 4.93 1.41 1.41" />
                          <path d="m17.66 17.66 1.41 1.41" />
                          <path d="M2 12h2" />
                          <path d="M20 12h2" />
                          <path d="m6.34 17.66-1.41 1.41" />
                          <path d="m19.07 4.93-1.41 1.41" />
                        </svg>
                      )}
                    </span>
                  </button>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="color"
          render={({ field }) => (
            <FormItem className="space-y-1">
              <FormLabel>Color Palette</FormLabel>
              <FormDescription>
                Select the color palette for the dashboard.
              </FormDescription>
              <FormMessage />
              <RadioGroup
                onValueChange={field.onChange}
                value={field.value}
                className="grid max-w-md grid-cols-3 gap-4 pt-2"
              >
                <FormItem>
                  <FormLabel className="[&:has([data-state=checked])>div]:border-primary">
                    <FormControl>
                      <RadioGroupItem value="default" className="sr-only" />
                    </FormControl>
                    <div className="border-muted hover:border-accent items-center rounded-md border-2 p-1">
                      <div className="bg-background space-y-2 rounded-sm p-2">
                        <div className="bg-card space-y-2 rounded-md p-2 shadow-sm">
                          <div className="bg-primary h-2 w-[80px] rounded-lg" />
                          <div className="bg-muted h-2 w-[100px] rounded-lg" />
                        </div>
                        <div className="bg-card flex items-center space-x-2 rounded-md p-2 shadow-sm">
                          <div className="bg-primary h-4 w-4 rounded-full" />
                          <div className="bg-muted h-2 w-[100px] rounded-lg" />
                        </div>
                      </div>
                    </div>
                    <span className="block w-full p-2 text-center font-normal">
                      Default
                    </span>
                  </FormLabel>
                </FormItem>

                <FormItem>
                  <FormLabel className="[&:has([data-state=checked])>div]:border-primary">
                    <FormControl>
                      <RadioGroupItem value="nature" className="sr-only" />
                    </FormControl>
                    <div className="border-muted hover:border-accent nature items-center rounded-md border-2 p-1">
                      <div className="bg-background space-y-2 rounded-sm p-2">
                        <div className="bg-card space-y-2 rounded-md p-2 shadow-sm">
                          <div className="bg-primary h-2 w-[80px] rounded-lg" />
                          <div className="bg-secondary h-2 w-[100px] rounded-lg" />
                        </div>
                        <div className="bg-card flex items-center space-x-2 rounded-md p-2 shadow-sm">
                          <div className="bg-accent h-4 w-4 rounded-full" />
                          <div className="bg-destructive h-2 w-[100px] rounded-lg" />
                        </div>
                      </div>
                    </div>
                    <span className="block w-full p-2 text-center font-normal">
                      Nature
                    </span>
                  </FormLabel>
                </FormItem>

                <FormItem>
                  <FormLabel className="[&:has([data-state=checked])>div]:border-primary">
                    <FormControl>
                      <RadioGroupItem value="retro" className="sr-only" />
                    </FormControl>
                    <div className="border-muted hover:border-accent retro items-center rounded-md border-2 p-1">
                      <div className="bg-background space-y-2 rounded-sm p-2">
                        <div className="bg-card space-y-2 rounded-md p-2 shadow-sm">
                          <div className="bg-primary h-2 w-[80px] rounded-lg" />
                          <div className="bg-secondary h-2 w-[100px] rounded-lg" />
                        </div>
                        <div className="bg-card flex items-center space-x-2 rounded-md p-2 shadow-sm">
                          <div className="bg-accent h-4 w-4 rounded-full" />
                          <div className="bg-destructive h-2 w-[100px] rounded-lg" />
                        </div>
                      </div>
                    </div>
                    <span className="block w-full p-2 text-center font-normal">
                      Retro
                    </span>
                  </FormLabel>
                </FormItem>
              </RadioGroup>
            </FormItem>
          )}
        />

        <Button type="submit">Update preferences</Button>
      </form>
    </Form>
  );
}
